import * as React from "react";
import { CommentPopover, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

// A 2x2 solid-teal PNG as a data URI - stands in for an uploaded screenshot so
// the attachment preview renders without a network fetch.
const SAMPLE_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAEklEQVR42mNk+M9Qz0BkYBxVSAYAQ7gB/3T0ZRkAAAAASUVORK5CYII=";

export function CommentPopoverDemo() {
  // Stubbed persistence seams. The component owns the UX, not the storage:
  // onSubmit receives { text, imageUrl }; onUploadImage returns a public URL.
  const handleSubmit = React.useCallback(
    (payload: { text: string; imageUrl?: string | null }) => {
      // eslint-disable-next-line no-console
      console.log("comment submitted", payload);
    },
    [],
  );

  const handleUpload = React.useCallback(async () => SAMPLE_IMAGE, []);

  return (
    <Section
      id="comment-popover"
      title="CommentPopover"
      description="Popover composer for adding a comment to an inline edit, a clause, or any attribution chip. Folds the brand Popover into a comment shell: author header, auto-grow textarea, an optional image-paste / attach affordance, and Cancel / Post controls (Cmd/Ctrl+Enter posts). It owns the UX, not persistence - onSubmit receives { text, imageUrl } and onUploadImage returns a stored URL. The Popover portals to the body, traps focus, and dismisses on Escape / click-outside."
    >
      <VariantBlock label="Default" caption="click to open; the footer shows the Cmd+V paste hint, Post is disabled until there is text">
        <CommentPopover authorName="Ada Lovelace" onSubmit={handleSubmit}>
          <Button variant="outline">Add comment</Button>
        </CommentPopover>
      </VariantBlock>

      <VariantBlock label="Editing a draft" caption="seeded via defaultValue, so Post is enabled immediately">
        <CommentPopover
          authorName="Ada Lovelace"
          defaultValue="This clause needs the renewal window spelled out."
          onSubmit={handleSubmit}
        >
          <Button variant="outline">Edit comment</Button>
        </CommentPopover>
      </VariantBlock>

      <VariantBlock label="With image upload" caption="onUploadImage wired - the footer gains Attach image and Cmd/Ctrl+V paste">
        <CommentPopover
          authorName="Ada Lovelace"
          onSubmit={handleSubmit}
          onUploadImage={handleUpload}
        >
          <Button variant="outline">Comment with attachment</Button>
        </CommentPopover>
      </VariantBlock>

      <VariantBlock label="Placement" caption="side / align pass through to the underlying Radix Popover">
        <CommentPopover
          authorName="Grace Hopper"
          side="bottom"
          align="end"
          submitLabel="Reply"
          onSubmit={handleSubmit}
        >
          <Button variant="ghost">Reply (bottom / end)</Button>
        </CommentPopover>
      </VariantBlock>
    </Section>
  );
}
